import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle, Phone, Mail, MapPin, Clock, Globe } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      toast.success("Message sent! I'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call me directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-transition" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl space-y-8">
            <div className="space-y-4">
              <div className="gold-line" />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white">
                Contact <span className="text-[#D4AF37]">Raad</span>
              </h1>
            </div>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Reach out. I actually answer my phone.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="space-y-12">
              <div className="space-y-8">
                <h2 className="text-3xl font-bold text-white">
                  Let's Talk Real Estate
                </h2>
                <p className="text-zinc-400 leading-relaxed">
                  Whether you're ready to buy, sell, or just have questions
                  about the market — I'm here to help. No pressure, no sales
                  pitch, just straight answers.
                </p>
              </div>

              <div className="space-y-6">
                <a
                  href="tel:+19092335411"
                  className="flex items-center gap-4 p-6 card-dark hover:border-[#D4AF37]/50 transition-colors group"
                  data-testid="contact-phone-link"
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <Phone className="w-6 h-6 text-[#D4AF37] group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Call or Text</p>
                    <p className="text-xl font-bold text-white">909-233-5411</p>
                  </div>
                </a>

                <a
                  href="mailto:raadshalabi41@gmail.com"
                  className="flex items-center gap-4 p-6 card-dark hover:border-[#D4AF37]/50 transition-colors group"
                  data-testid="contact-email-link"
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <Mail className="w-6 h-6 text-[#D4AF37] group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Email</p>
                    <p className="text-xl font-bold text-white">
                      raadshalabi41@gmail.com
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-6 card-dark">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Office</p>
                    <p className="text-lg font-bold text-white">RE/MAX TIME</p>
                    <p className="text-zinc-400 text-sm">10535 E Foothill Blvd #460</p>
                    <p className="text-zinc-400 text-sm">Rancho Cucamonga, CA 91730</p>
                  </div>
                </div>

                <a
                  href="https://theshalabigroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-6 card-dark hover:border-[#D4AF37]/50 transition-colors group"
                  data-testid="contact-website-link"
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors">
                    <Globe className="w-6 h-6 text-[#D4AF37] group-hover:text-black transition-colors" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Website</p>
                    <p className="text-xl font-bold text-white">theshalabigroup.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-6 card-dark">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Availability</p>
                    <p className="text-xl font-bold text-white">7 Days a Week</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-4">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/RE-MAX_logo.png/320px-RE-MAX_logo.png" 
                  alt="RE/MAX" 
                  className="h-10 object-contain"
                />
                <div>
                  <p className="text-white font-semibold">
                    The Shalabi Real Estate Group
                  </p>
                  <p className="text-zinc-500 text-sm">RE/MAX TIME | DRE #01905641</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="card-dark p-8 md:p-12 border-[#D4AF37]/30">
              {success ? (
                <div
                  className="text-center py-12 animate-fadeIn"
                  data-testid="contact-form-success"
                >
                  <CheckCircle className="w-20 h-20 text-[#D4AF37] mx-auto mb-6" />
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-zinc-400 mb-8">
                    I'll get back to you as soon as possible. If it's urgent,
                    feel free to call me directly.
                  </p>
                  <Button
                    onClick={() => setSuccess(false)}
                    className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none font-bold uppercase tracking-widest"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-white">
                      Send Me a Message
                    </h3>
                    <p className="text-zinc-400 text-sm">
                      I'll respond within 24 hours — usually much faster.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                      className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
                      data-testid="contact-form-name"
                    />
                    <Input
                      type="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      required
                      className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
                      data-testid="contact-form-email"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 h-12"
                      data-testid="contact-form-phone"
                    />
                    <Textarea
                      placeholder="How can I help you?"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      required
                      rows={5}
                      className="bg-[#0A0A0A] border-white/20 rounded-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] placeholder:text-zinc-600 px-4 py-3 resize-none"
                      data-testid="contact-form-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest h-14"
                    data-testid="contact-form-submit"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
